import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Printer, Loader2, FileText } from "lucide-react";
import axios from "axios";

/** Publieke share-pagina — géén auth. De klant opent deze link uit de e-mail
 *  (frontendUrl/share/:token). De HTML wordt via de publieke backend-route
 *  /api/share/:token opgehaald en in een sandboxed iframe gerenderd. Een <base>
 *  wordt geïnjecteerd zodat logo's (/uploads/...) laden vanaf de backend. */
export default function ShareView() {
  const { token } = useParams<{ token: string }>();
  const [html, setHtml] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    if (!token) {
      setError("Geen geldige link.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    setHtml("");
    const apiBase = import.meta.env.VITE_API_URL || "";
    axios
      .get(`${apiBase}/api/share/${token}`, { responseType: "text", transformResponse: (r) => r })
      .then((res) => {
        const raw = typeof res.data === "string" ? res.data : "";
        // Backend injecteert al een absolute <base>; dubbele <base> is ongevaarlijk
        // (eerste wint), maar we zorgen er hier voor het-zeker-zeker voor srcDoc.
        const base = apiBase.endsWith("/") ? apiBase : apiBase + "/";
        const withBase =
          base && raw.includes("<head>")
            ? raw.replace("<head>", `<head><base href="${base}">`)
            : raw;
        setHtml(withBase);
      })
      .catch((err: any) => {
        const status = err?.response?.status;
        if (status === 404) {
          setError(err?.response?.data || "Dit document is niet (meer) beschikbaar of de link is verlopen.");
        } else {
          setError("Document kon niet worden geladen.");
        }
      })
      .finally(() => setLoading(false));
  }, [token]);

  const handlePrint = () => {
    try {
      iframeRef.current?.contentWindow?.focus();
      iframeRef.current?.contentWindow?.print();
    } catch {
      if (html) {
        const w = window.open("", "_blank");
        if (w) {
          w.document.write(html);
          w.document.close();
        }
      }
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-slate-700">
            <FileText size={24} className="text-brand-500" />
            <span className="font-bold">AIBoek</span>
            <span className="text-slate-400 font-normal">· document</span>
          </div>
          <button
            onClick={handlePrint}
            disabled={loading || !!error || !html}
            className="inline-flex items-center gap-2 rounded-xl bg-brand-500 px-3.5 py-2 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <Printer size={20} /> Afdrukken / PDF
          </button>
        </div>
      </header>

      <div className="flex-1 overflow-auto p-4">
        <div className="max-w-5xl mx-auto">
          {loading && (
            <div className="flex flex-col items-center justify-center text-slate-500 py-24">
              <Loader2 size={40} className="animate-spin mb-3" />
              <p className="text-sm font-medium">Document laden…</p>
            </div>
          )}
          {error && !loading && (
            <div className="flex flex-col items-center justify-center text-rose-600 py-24">
              <p className="text-sm font-medium text-center max-w-md">{error}</p>
            </div>
          )}
          {!loading && !error && html && (
            <iframe
              ref={iframeRef}
              title="document"
              srcDoc={html}
              sandbox="allow-popups allow-same-origin allow-modals allow-scripts"
              className="w-full bg-white rounded-xl border border-slate-200 shadow-sm"
              style={{ minHeight: "85vh" }}
            />
          )}
        </div>
      </div>
    </div>
  );
}